import { message } from 'antd';
import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWith409 from './baseQueryWith409';
import qs from 'qs';
import fetch from 'auth/FetchInterceptor';

// direct fetch without RTK Query
export const getProcesos = query =>
	fetch.get(
		`/procesos${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const getProceso = ({ id, query }) =>
	fetch.get(
		`/procesos/${id}${
			query
				? `?${qs.stringify(
						{ ...query, publicationState: 'live' },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const getCountProcesosByEstado = ({ query }) =>
	fetch.get(
		`/procesos/count-by-estado${
			query
				? `?${qs.stringify(
						{ start: query.start, end: query.end },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const getCountProcesosByCliente = ({ query }) =>
	fetch.get(
		`/procesos/count-by-cliente${
			query
				? `?${qs.stringify(
						{ start: query.start, end: query.end },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const getCountProcesosByReclutador = ({ query }) =>
	fetch.get(
		`/procesos/count-by-reclutador${
			query
				? `?${qs.stringify(
						{ start: query.start, end: query.end },
						{
							encodeValuesOnly: true,
						}
				  )}`
				: ''
		}`
	);
export const addProceso = data => fetch.post('/procesos', data);
export const updateProceso = ({ id, ...data }) =>
	fetch.put(`/procesos/${id}`, data);
export const deleteProceso = id => fetch.delete(`/procesos/${id}`);

// RTK Query
export const procesosApi = createApi({
	reducerPath: 'procesosApi',
	baseQuery: baseQueryWith409,

	// default configuration
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: true,
	refetchOnFocus: true,

	//endpoints
	endpoints: build => ({
		getProcesos: build.query({
			query: query => {
				return {
					url: `/procesos${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			transformResponse: response => response.data,
			providesTags: result =>
				// is result available?
				result
					? // successful query
					  [
							...result.map(({ id }) => ({ type: 'Procesos', id })),
							{ type: 'Procesos', id: 'LIST' },
					  ]
					: // an error occurred, but we still want to refetch this query when `{ type: 'Posts', id: 'LIST' }` is invalidated
					  [{ type: 'Procesos', id: 'LIST' }],
		}),
		getProceso: build.query({
			query: ({ id, query }) => {
				return {
					url: `/procesos/${id}${
						query
							? `?${qs.stringify(
									{ ...query, publicationState: 'live' },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
				} catch (error) {
					if (error.error.status === 404) {
						message.error('Proceso no encontrado');
					} else {
						message.error('Error al obtener proceso');
					}
					console.error(error.error.data?.error.message);
				}
			},
			transformResponse: response => response.data,
			providesTags: (result, error, id) => [{ type: 'Procesos', id }],
		}),
		getCountProcesosByEstado: build.query({
			query: query => {
				return {
					url: `/procesos/count-by-estado${
						query
							? `?${qs.stringify(
									{ start: query.start, end: query.end },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
		}),
		getCountProcesosByCliente: build.query({
			query: query => {
				return {
					url: `/procesos/count-by-cliente${
						query
							? `?${qs.stringify(
									{ start: query.start, end: query.end },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
		}),
		getCountProcesosByReclutador: build.query({
			query: query => {
				return {
					url: `/procesos/count-by-reclutador${
						query
							? `?${qs.stringify(
									{ start: query.start, end: query.end },
									{
										encodeValuesOnly: true,
									}
							  )}`
							: ''
					}`,
					method: 'GET',
				};
			},
		}),
		addProceso: build.mutation({
			query: data => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/procesos`,
					method: 'POST',
					body: JSON.stringify({
						data: {
							...data,
							created_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Proceso agregado exitosamente');
				} catch (error) {
					message.error('Error al agregar proceso');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: [{ type: 'Procesos', id: 'LIST' }],
		}),
		updateProceso: build.mutation({
			query: ({ id, ...data }) => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/procesos/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						data: {
							...data,
							updated_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Proceso actualizado exitosamente');
				} catch (error) {
					message.error('Error al actualizar proceso');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, { id }) => [{ type: 'Procesos', id }],
		}),
		deleteProceso: build.mutation({
			query: id => {
				const user = JSON.parse(localStorage.getItem('me'));
				return {
					url: `/procesos/${id}`,
					method: 'PUT',
					body: JSON.stringify({
						data: {
							publishedAt: null,
							updated_by_user: user.id,
						},
					}),
				};
			},
			onQueryStarted: async (arg, { dispatch, queryFulfilled }) => {
				// try catch with antd message when fulfilled and rejected
				try {
					await queryFulfilled;
					message.success('Proceso eliminado exitosamente');
				} catch (error) {
					message.error('Error al eliminar proceso');
					if (error.error.data?.error) {
						message.error(error.error.data?.error.message);
					}
				}
			},
			invalidatesTags: (result, error, id) => [{ type: 'Procesos', id }],
		}),
	}),
});

export const {
	useGetProcesosQuery,
	useGetProcesoQuery,
	useGetCountProcesosByEstadoQuery,
	useGetCountProcesosByClienteQuery,
	useGetCountProcesosByReclutadorQuery,
	useAddProcesoMutation,
	useUpdateProcesoMutation,
	useDeleteProcesoMutation,
} = procesosApi;
